import { useState, useEffect, useRef, useCallback } from 'react';
import { useLang } from '../context/LanguageContext';
import { useNewsData } from '../context/NewsDataContext';
import { useSubscriptionGate } from '../context/SubscriptionGateContext';
import StoryExcerpt from './StoryExcerpt';
import { excerptCardPreview } from '../utils/storyText.js';
import { formatTimeAgo } from '../utils/timeAgo';
import { shareStoryOnWhatsApp } from '../utils/storyShare';
import { isBookmarked, toggleBookmark } from '../utils/bookmarks';
import { isNativeApp } from '../utils/platform.js';
import { hapticSelection } from '../utils/haptics';
import { setStoryUrl, clearStoryUrl, storyPath } from '../utils/storyLinks';
import { usePageMeta } from '../hooks/usePageMeta';
import { trackEvent } from '../utils/analytics';
import {
  STORY_CATEGORIES,
  resolveStoryCategoryId,
  storyCategoryLabel,
} from '../config/storyCategories.js';
import { pickTeEn } from '../utils/localizedContent.js';
import { mediaUrl, API_URL as API } from '../config/env.js';
import './ArticlesSection.css';

const ALL_CATEGORY = '__all__';
const EDITORS_PICK_AUTO_MS = 5000;

function EditorsPickCarousel({
  stories,
  display,
  cardBlurb,
  lang,
  t,
  hasFullAccess,
  freeStoryId,
  onOpen,
  onKey,
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef(null);
  const count = stories.length;
  const storyKey = stories.map((s) => s.id).join(',');

  useEffect(() => {
    setIndex(0);
  }, [count, storyKey]);

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, EDITORS_PICK_AUTO_MS);
    return () => clearInterval(id);
  }, [count, paused]);

  if (!count) return null;

  const go = (next) => {
    if (count <= 1) return;
    setIndex(((next % count) + count) % count);
  };

  const onTouchStart = (e) => {
    touchX.current = e.changedTouches[0]?.clientX ?? null;
  };

  const onTouchEnd = (e) => {
    if (touchX.current == null || count <= 1) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchX.current) - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) < 40) return;
    go(index + (dx < 0 ? 1 : -1));
    setPaused(true);
    window.setTimeout(() => setPaused(false), EDITORS_PICK_AUTO_MS);
  };

  return (
    <div
      className="art-featured-zone"
      data-sub-free
      aria-roledescription="carousel"
      aria-label={t.editorsPickBadge || "Editor's pick"}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false);
      }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="art-featured-head">
        <span className="art-featured-label">{t.editorsPickBadge}</span>
        {count > 1 && (
          <span className="art-featured-count">
            {index + 1} / {count}
          </span>
        )}
      </div>

      <div className="art-featured-carousel">
        {count > 1 && (
          <button
            type="button"
            className="art-featured-nav art-featured-nav--prev"
            aria-label="Previous story"
            onClick={() => go(index - 1)}
          >
            ‹
          </button>
        )}

        <div className="art-featured-viewport">
          <div
            className="art-featured-track"
            style={{ transform: `translateX(-${index * 100}%)` }}
          >
            {stories.map((s, i) => (
              <div
                key={s.id}
                className="art-featured-slide"
                role="group"
                aria-roledescription="slide"
                aria-label={`${i + 1} of ${count}`}
                aria-hidden={i !== index}
              >
                <StoryCard
                  story={s}
                  display={display}
                  cardBlurb={cardBlurb}
                  lang={lang}
                  t={t}
                  hasFullAccess={hasFullAccess}
                  freeStoryId={freeStoryId}
                  onOpen={onOpen}
                  onKey={onKey}
                  variant="featured"
                  TitleTag="h3"
                  priority={i === index || i === 0}
                />
              </div>
            ))}
          </div>
        </div>

        {count > 1 && (
          <button
            type="button"
            className="art-featured-nav art-featured-nav--next"
            aria-label="Next story"
            onClick={() => go(index + 1)}
          >
            ›
          </button>
        )}
      </div>

      {count > 1 && (
        <div className="art-featured-dots" role="tablist" aria-label="Editor's pick slides">
          {stories.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={`Story ${i + 1}`}
              className={`art-featured-dot${i === index ? ' is-active' : ''}`}
              onClick={() => setIndex(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function cardAspectClass(aspect) {
  if (aspect === 'portrait') return 'art-story-cover--portrait';
  if (aspect === 'square') return 'art-story-cover--square';
  return 'art-story-cover--landscape';
}

function DialogLeadImage({ src, alt, layout = 'landscape' }) {
  const [failed, setFailed] = useState(false);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    setFailed(false);
    setTries(0);
  }, [src]);
  if (failed || !src) {
    return (
      <div className="art-dialog-img art-dialog-cover-fallback" role="img" aria-label={alt}>
        <span aria-hidden="true">📰</span>
      </div>
    );
  }
  const url = tries ? `${src}${src.includes('?') ? '&' : '?'}_rk=${tries}` : src;
  return (
    <img
      src={url}
      alt={alt}
      className={`art-dialog-img art-dialog-img--${layout === 'portrait' ? 'portrait' : 'landscape'}`}
      loading="eager"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={() => {
        if (tries < 2) setTries((n) => n + 1);
        else setFailed(true);
      }}
    />
  );
}

function StoryCover({ src, alt, cardAspect = 'landscape', priority = false }) {
  const [failed, setFailed] = useState(false);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    setFailed(false);
    setTries(0);
  }, [src]);
  const url = src && tries ? `${src}${src.includes('?') ? '&' : '?'}_rk=${tries}` : src;
  return (
    <div className={`art-story-cover ${cardAspectClass(cardAspect)}`}>
      {src && !failed ? (
        <img
          src={url}
          alt={alt}
          className="art-story-img"
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          fetchPriority={priority ? 'high' : 'auto'}
          referrerPolicy="no-referrer"
          onError={() => {
            if (tries < 2) setTries((n) => n + 1);
            else setFailed(true);
          }}
        />
      ) : (
        <div className="art-story-cover-placeholder" role="img" aria-label={alt}>
          <span aria-hidden="true">📰</span>
        </div>
      )}
    </div>
  );
}

function plainStoryText(html) {
  return String(html || '')
    .replace(/<br\s*\/?>/gi, '. ')
    .replace(/<\/p>/gi, '. ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .replace(/\s*\.\s*\./g, '.')
    .trim();
}

/** Split for Google Translate TTS (~180 char limit) on Telugu sentence boundaries. */
function chunkForTts(text, maxLen = 170) {
  const clean = plainStoryText(text);
  if (!clean) return [];
  if (clean.length <= maxLen) return [clean];
  const parts = [];
  let rest = clean;
  while (rest.length > maxLen) {
    let cut = -1;
    for (const sep of ['।', '.', '!', '?', ',', ' ']) {
      const idx = rest.lastIndexOf(sep, maxLen);
      if (idx > 40) {
        cut = idx + 1;
        break;
      }
    }
    if (cut < 0) cut = maxLen;
    parts.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) parts.push(rest);
  return parts.filter(Boolean);
}

function useSpeech(lang = 'te') {
  const [speaking, setSpeaking] = useState(false);
  const [supported] = useState(true);
  const cancelRef = useRef(false);
  const audioRef = useRef(null);

  const stop = useCallback(() => {
    cancelRef.current = true;
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = '';
      } catch {
        /* ignore */
      }
      audioRef.current = null;
    }
    try {
      window.speechSynthesis?.cancel();
    } catch {
      /* ignore */
    }
    setSpeaking(false);
  }, []);

  const playUrl = (url) =>
    new Promise((resolve, reject) => {
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => resolve();
      audio.onerror = () => reject(new Error('audio error'));
      const p = audio.play();
      if (p && typeof p.catch === 'function') p.catch(reject);
    });

  const speakGoogle = async (text) => {
    const tl = lang === 'hi' || lang === 'ta' || lang === 'kn' || lang === 'en' ? lang : 'te';
    const base = API || 'https://repatikosam.com';
    const chunks = chunkForTts(text);
    if (!chunks.length) throw new Error('empty');
    for (const chunk of chunks) {
      if (cancelRef.current) break;
      const url = `${base}/api/tts?lang=${encodeURIComponent(tl)}&text=${encodeURIComponent(chunk)}`;
      await playUrl(url);
    }
  };

  const speakWeb = (text) =>
    new Promise((resolve) => {
      if (!('speechSynthesis' in window)) {
        resolve();
        return;
      }
      window.speechSynthesis.cancel();
      const utt = new SpeechSynthesisUtterance(plainStoryText(text));
      const voices = window.speechSynthesis.getVoices();
      const prefer =
        voices.find((v) => v.lang === 'te-IN') ||
        voices.find((v) => v.lang.startsWith('te')) ||
        voices.find((v) => v.lang === 'hi-IN') ||
        null;
      if (prefer) {
        utt.voice = prefer;
        utt.lang = prefer.lang;
      } else {
        utt.lang = lang === 'en' ? 'en-IN' : 'te-IN';
      }
      utt.rate = 0.92;
      utt.onend = () => resolve();
      utt.onerror = () => resolve();
      window.speechSynthesis.speak(utt);
    });

  const speak = useCallback(
    async (text) => {
      const payload = plainStoryText(text);
      if (!payload) return;
      stop();
      cancelRef.current = false;
      setSpeaking(true);
      try {
        await speakGoogle(payload);
      } catch {
        if (!cancelRef.current) {
          try {
            await speakWeb(payload);
          } catch {
            /* ignore */
          }
        }
      } finally {
        if (!cancelRef.current) setSpeaking(false);
        audioRef.current = null;
      }
    },
    [lang, stop],
  );

  useEffect(() => () => stop(), [stop]);
  return { speaking, supported, speak, stop };
}

function StoriesGrid({
  stories,
  display,
  cardBlurb,
  lang,
  t,
  hasFullAccess,
  freeStoryId,
  onOpen,
  onKey,
}) {
  if (!stories.length) return null;

  return (
    <div className="art-strip" data-sub-free>
      <div className="art-strip__grid" role="list">
        {stories.map((s, i) => (
          <StoryCard
            key={s.id}
            story={s}
            display={display}
            cardBlurb={cardBlurb}
            lang={lang}
            t={t}
            hasFullAccess={hasFullAccess}
            freeStoryId={freeStoryId}
            onOpen={onOpen}
            onKey={onKey}
            variant="strip"
            TitleTag="h3"
            priority={i < 2}
          />
        ))}
      </div>
    </div>
  );
}

function StoryCard({
  story,
  display,
  cardBlurb,
  lang,
  t,
  hasFullAccess,
  freeStoryId,
  onOpen,
  onKey,
  variant = 'grid',
  TitleTag = 'h4',
  priority = false,
}) {
  const d = display(story);
  const isFeaturedVariant = variant === 'featured';
  const isStrip = variant === 'strip';
  const cardClass = isFeaturedVariant
    ? 'art-featured-card'
    : isStrip
      ? 'art-strip-card'
      : 'art-story-card';
  const coverSrc = mediaUrl(story.cardImage || story.image);

  return (
    <div
      className={cardClass}
      onClick={() => onOpen(story.id)}
      onKeyDown={(e) => onKey(e, story.id)}
      role="button"
      tabIndex={0}
      data-sub-free={!hasFullAccess && story.id === freeStoryId ? true : undefined}
    >
      <StoryCover
        src={coverSrc}
        alt={d.title}
        cardAspect="landscape"
        priority={priority}
      />
      <div
        className={`art-story-body ${
          isFeaturedVariant
            ? 'art-story-body--featured'
            : isStrip
              ? 'art-story-body--strip'
              : 'art-story-body--grid'
        }`}
      >
        <TitleTag className="art-story-title">{d.title}</TitleTag>
        {isFeaturedVariant && <p className="art-story-excerpt">{cardBlurb(story)}</p>}
        {isFeaturedVariant && <span className="art-read-more">{t.readMore} →</span>}
      </div>
      {!hasFullAccess && story.id === freeStoryId && (
        <span className="art-free-badge">{t.freeReadBadge}</span>
      )}
      {!hasFullAccess && story.id !== freeStoryId && (
        <span className="art-lock-badge">{t.subscribersOnlyBadge}</span>
      )}
    </div>
  );
}

export default function ArticlesSection({ initialStoryId = null, onClose = null }) {
  const { t, lang } = useLang();
  const { stories, loading: storiesLoading, feedError, refresh } = useNewsData();
  const { hasFullAccess, openSubscribe } = useSubscriptionGate();
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY);
  const [openStory, setOpenStory] = useState(null);
  const [usefulThanks, setUsefulThanks] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);

  const { speaking, supported, speak, stop } = useSpeech(lang);
  const openedFromProp = useRef(false);

  const display = useCallback(
    (s) => {
      const catId = resolveStoryCategoryId(s);
      return {
        title: pickTeEn(lang, s.title, s.titleEn),
        excerpt: pickTeEn(lang, s.excerpt, s.excerptEn),
        body: pickTeEn(
          lang,
          s.body || s.excerpt,
          s.bodyEn || s.excerptEn,
        ),
        category: catId
          ? storyCategoryLabel(catId, lang)
          : pickTeEn(lang, s.category, s.categoryEn),
      };
    },
    [lang]
  );

  const storyMeta = openStory ? display(openStory) : null;
  usePageMeta({
    title: storyMeta ? `${storyMeta.title} | ${t.brandName}` : t.brandName,
    description: storyMeta
      ? (storyMeta.excerpt || storyMeta.body || '').slice(0, 160)
      : t.paperSub,
    path: openStory ? storyPath(openStory.id) : '/',
    image: openStory?.image ? `/images/${openStory.id}/og.jpg` : '',
    enabled: !!openStory && !isNativeApp(),
  });

  const cardBlurb = (s) => {
    const d = display(s);
    const short = (d.excerpt || '').trim();
    return short || excerptCardPreview(d.body);
  };

  const openById = useCallback(
    (id) => {
      const s = stories.find((x) => x.id === id);
      if (!s) return;
      const isFree = id === stories[0]?.id;
      if (!hasFullAccess && !isFree) {
        openSubscribe();
        return;
      }
      setOpenStory(s);
      setUsefulThanks(false);
      setBookmarked(isBookmarked(s.id));
      setStoryUrl(id);
      trackEvent('view_story', { story_id: id });
    },
    [stories, hasFullAccess, openSubscribe]
  );

  const onStoryKey = (e, id) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openById(id);
    }
  };

  const closeStory = useCallback(() => {
    setOpenStory(null);
    stop();
    clearStoryUrl();
    onClose?.();
  }, [stop, onClose]);

  useEffect(() => {
    openedFromProp.current = false;
  }, [initialStoryId]);

  useEffect(() => {
    if (!initialStoryId || openedFromProp.current) return;
    if (!stories.some((x) => x.id === initialStoryId)) return;
    openedFromProp.current = true;
    openById(initialStoryId);
  }, [initialStoryId, stories, openById]);

  useEffect(() => {
    setActiveCategory(ALL_CATEGORY);
  }, [lang]);

  useEffect(() => {
    if (!openStory) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeStory();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openStory, closeStory]);

  const categoryKeys = [ALL_CATEGORY, ...STORY_CATEGORIES.map((c) => c.id)];
  const categoryLabel = (key) => {
    if (key === ALL_CATEGORY) return t.catAll || 'అన్నీ';
    if (key === 'politics') return t.catPolitics || storyCategoryLabel(key, lang);
    if (key === 'news') return t.catNews || storyCategoryLabel(key, lang);
    if (key === 'turmeric') return t.catTurmeric || storyCategoryLabel(key, lang);
    return storyCategoryLabel(key, lang);
  };

  const storyCatId = (s) => resolveStoryCategoryId(s);

  const filtered =
    activeCategory === ALL_CATEGORY
      ? stories
      : stories.filter((s) => storyCatId(s) === activeCategory);

  const featuredList = filtered.filter((s) => s.featured);
  const rest = filtered.filter((s) => !s.featured);

  const freeStoryId = stories[0]?.id;

  const relatedStories =
    openStory && stories.length > 1
      ? stories
          .filter((s) => s.id !== openStory.id && storyCatId(s) && storyCatId(s) === storyCatId(openStory))
          .slice(0, 3)
      : [];

  function markUseful() {
    if (!openStory) return;
    try {
      localStorage.setItem(`rk-useful-${openStory.id}`, '1');
    } catch {
      /* ignore */
    }
    setUsefulThanks(true);
    trackEvent('story_useful', { story_id: openStory.id });
  }

  if (storiesLoading) {
    return (
      <section className="articles-section articles-section--loading" id="articles" aria-busy="true">
        <div className="art-header">
          <div className="art-title-row">
            <div className="art-accent" />
            <h2>{t.articlesTitle}</h2>
          </div>
        </div>
        <p className="articles-section__loading-msg">{t.loading}</p>
      </section>
    );
  }

  if (stories.length === 0) {
    return (
      <section
        className={`articles-section ${feedError ? 'articles-section--error' : 'articles-section--empty'}`}
        id="articles"
      >
        <div className="art-header">
          <div className="art-title-row">
            <div className="art-accent" />
            <h2>{t.articlesTitle}</h2>
          </div>
        </div>
        <p className="articles-section__empty-msg" role={feedError ? 'alert' : undefined}>
          {feedError ? t.feedErrorMsg : t.noStoriesYet}
        </p>
        {feedError && (
          <div className="articles-section__retry-wrap">
            <button type="button" className="articles-section__retry" onClick={refresh}>
              {t.feedErrorRetry}
            </button>
          </div>
        )}
      </section>
    );
  }

  return (
    <section className="articles-section" id="articles">
      <div className="art-header">
        <div className="art-title-row">
          <div className="art-accent" />
          <h2>{t.articlesTitle}</h2>
        </div>
        <div className="art-cats" data-sub-free>
          {categoryKeys.map((c) => (
            <button
              key={c}
              type="button"
              className={`art-cat-btn ${activeCategory === c ? 'active' : ''}`}
              onClick={() => setActiveCategory(c)}
            >
              {categoryLabel(c)}
            </button>
          ))}
        </div>
      </div>

      <div className="art-stories-grid">
        {featuredList.length > 0 && (
          <EditorsPickCarousel
            stories={featuredList}
            display={display}
            cardBlurb={cardBlurb}
            lang={lang}
            t={t}
            hasFullAccess={hasFullAccess}
            freeStoryId={freeStoryId}
            onOpen={openById}
            onKey={onStoryKey}
          />
        )}

        {rest.length > 0 && (
          <StoriesGrid
            stories={rest}
            display={display}
            cardBlurb={cardBlurb}
            lang={lang}
            t={t}
            hasFullAccess={hasFullAccess}
            freeStoryId={freeStoryId}
            onOpen={openById}
            onKey={onStoryKey}
          />
        )}
      </div>

      {openStory && (
        <div
          className={`art-dialog-backdrop${isNativeApp() ? ' art-dialog-backdrop--ios' : ''}`}
          onClick={closeStory}
        >
          <article
            className={`art-dialog art-dialog--reader-${openStory.readerLayout === 'portrait' ? 'portrait' : 'landscape'}${isNativeApp() ? ' art-dialog--ios' : ''}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="art-dialog-title"
          >
            {!isNativeApp() ? (
              <>
                <div className="art-dialog__stripe" aria-hidden />
                <div className="art-dialog__handle" aria-hidden />
                <header className="art-dialog__top">
                  <div className="art-dialog__top-main">
                    <span className="art-dialog__cat art-dialog__cat--plain">
                      {display(openStory).category}
                    </span>
                    <span className="art-dialog__brand-pill">{t.brandName}</span>
                  </div>
                  <button type="button" className="art-dialog__close" onClick={closeStory} aria-label={t.closeBtn}>
                    <span aria-hidden>✕</span>
                  </button>
                </header>
              </>
            ) : (
              <header className="art-dialog__top art-dialog__top--ios">
                <button type="button" className="art-dialog__back" onClick={closeStory} aria-label={t.closeBtn}>
                  <svg viewBox="0 0 12 20" width="12" height="20" aria-hidden>
                    <path d="M10 2L2 10l8 8" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <p className="art-dialog__nav-title">{t.brandName}</p>
                <button type="button" className="art-dialog__done" onClick={closeStory}>
                  {t.nativeDone}
                </button>
              </header>
            )}

            <div className="art-dialog__body">
              {!isNativeApp() ? (
                <figure className="art-dialog__media">
                  <DialogLeadImage
                    src={mediaUrl(openStory.image || openStory.cardImage)}
                    alt={display(openStory).title}
                    layout={openStory.readerLayout === 'portrait' ? 'portrait' : 'landscape'}
                  />
                </figure>
              ) : null}

              <div className={`art-dialog__content${isNativeApp() ? ' art-dialog__sheet' : ''}`}>
                {isNativeApp() && (openStory.image || openStory.cardImage) ? (
                  <figure className="art-dialog__hero">
                    <DialogLeadImage
                      src={mediaUrl(openStory.image || openStory.cardImage)}
                      alt={display(openStory).title}
                      layout={openStory.readerLayout === 'portrait' ? 'portrait' : 'landscape'}
                    />
                  </figure>
                ) : null}

                <div className="art-dialog__title-block">
                  {!isNativeApp() ? <p className="art-dialog__kicker">{t.articlesTitle}</p> : null}
                  {isNativeApp() ? (
                    <div className="art-dialog__meta">
                      <span className="art-dialog__badge">{display(openStory).category}</span>
                      <time className="art-dialog__time" dateTime={openStory.createdAt || undefined}>
                        {openStory.createdAt ? formatTimeAgo(openStory.createdAt, lang) : ''}
                        {openStory.createdAt ? ' · ' : ''}
                        {Math.max(
                          1,
                          Math.round(
                            String(display(openStory).body || '')
                              .replace(/<[^>]+>/g, ' ')
                              .trim()
                              .split(/\s+/)
                              .filter(Boolean).length / 180,
                          ),
                        )}{' '}
                        min
                      </time>
                    </div>
                  ) : null}
                  <h2 id="art-dialog-title" className="art-dialog__title">
                    {display(openStory).title}
                  </h2>
                  {!isNativeApp() && openStory.createdAt ? (
                    <time className="art-dialog__time" dateTime={openStory.createdAt}>
                      {formatTimeAgo(openStory.createdAt, lang)}
                    </time>
                  ) : null}
                </div>

                <div className="art-dialog__prose">
                  <StoryExcerpt
                    text={display(openStory).body}
                    className="art-dialog-content"
                    paragraphClassName="art-dialog-para"
                  />
                </div>

                {relatedStories.length > 0 && (
                  <div className="art-dialog__related">
                    <h3 className="art-dialog__related-title">{t.relatedStoriesTitle}</h3>
                    <ul>
                      {relatedStories.map((s) => (
                        <li key={s.id}>
                          <button type="button" onClick={() => openById(s.id)}>
                            {display(s).title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {!isNativeApp() ? (
                  <div className="art-dialog__useful">
                    <p>{t.storyUsefulPrompt}</p>
                    {usefulThanks ? (
                      <span className="art-dialog__useful-thanks">{t.storyUsefulThanks}</span>
                    ) : (
                      <button type="button" className="art-dialog__useful-btn" onClick={markUseful}>
                        👍 {t.storyUsefulYes}
                      </button>
                    )}
                  </div>
                ) : usefulThanks ? (
                  <p className="art-dialog__useful-thanks art-dialog__useful-thanks--ios">{t.storyUsefulThanks}</p>
                ) : (
                  <button type="button" className="art-dialog__react" onClick={markUseful}>
                    {t.storyUsefulYes}
                  </button>
                )}
              </div>
            </div>

            <footer className={`art-dialog__foot${isNativeApp() ? ' art-dialog__dock' : ''}`}>
              {isNativeApp() ? (
                <div className="art-dialog__dock-bar">
                  <button
                    type="button"
                    className={`art-dialog__tool${bookmarked ? ' is-on' : ''}`}
                    onClick={() => {
                      const on = toggleBookmark(openStory);
                      setBookmarked(on);
                      void hapticSelection();
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="22" height="22" fill={bookmarked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.7" aria-hidden>
                      <path d="M7 4.5h10a1.5 1.5 0 0 1 1.5 1.5v14l-6.5-3.4L5.5 20V6A1.5 1.5 0 0 1 7 4.5z" strokeLinejoin="round" />
                    </svg>
                    <span>{bookmarked ? t.nativeBookmarkSaved : t.nativeBookmark}</span>
                  </button>
                  <button
                    type="button"
                    className="art-dialog__tool"
                    onClick={() => shareStoryOnWhatsApp(display(openStory).title, openStory.id, lang)}
                  >
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <circle cx="18" cy="5" r="2.5" />
                      <circle cx="6" cy="12" r="2.5" />
                      <circle cx="18" cy="19" r="2.5" />
                      <path d="M8.4 13.2l7.2 3.9M15.6 6.9l-7.2 3.9" />
                    </svg>
                    <span>{t.nativeShare}</span>
                  </button>
                  <button
                    type="button"
                    className={`art-dialog__tool art-dialog__tool--listen${speaking ? ' is-on' : ''}`}
                    onClick={() =>
                      speaking ? stop() : speak(`${display(openStory).title}. ${display(openStory).body}`)
                    }
                    disabled={!supported}
                  >
                    <span className="art-dialog__listen-orb" aria-hidden>
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        {speaking ? (
                          <path d="M7 6h3.5v12H7V6zm6.5 0H17v12h-3.5V6z" />
                        ) : (
                          <path d="M4 10v4h3.2L12 18.5V5.5L7.2 10H4zm12.2 1a3.2 3.2 0 0 1 0 2.1v-2.1zm2.3-2.6v7.2a5.5 5.5 0 0 0 0-7.2z" />
                        )}
                      </svg>
                    </span>
                    <span>{speaking ? t.stopListen : t.listenBtn}</span>
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="art-dialog__action art-dialog__action--share"
                    onClick={() => shareStoryOnWhatsApp(display(openStory).title, openStory.id, lang)}
                  >
                    {t.shareStoryBtn}
                  </button>
                  <button
                    type="button"
                    className={`art-dialog__action art-dialog__action--listen ${speaking ? 'art-dialog__listen--on' : ''}`}
                    onClick={() =>
                      speaking ? stop() : speak(`${display(openStory).title}. ${display(openStory).body}`)
                    }
                    disabled={!supported}
                  >
                    {speaking ? t.stopListen : t.listenBtn}
                  </button>
                </>
              )}
            </footer>
          </article>
        </div>
      )}
    </section>
  );
}
